import type {
  DiversificationCategory,
  DiversificationItem,
  DiversificationResponse,
  DiversificationScheme,
} from '@/store/diversificationsApi';
import { PortfolioDiversificationIdNumber } from '@/utils/enums';

function normalizeDiversificationName(name: string | undefined): string {
  return (name ?? '').trim().toLowerCase();
}

/** API may return drilldown as camelCase or PascalCase `Drilldown`. */
export function recordHasDiversificationDrilldown(
  record: DiversificationItem,
): boolean {
  if (record.drilldown) return true;
  const raw = record as unknown as Record<string, unknown>;
  return raw.Drilldown === true;
}

/**
 * After loading a bar-chart drilldown level: switch to the holdings table when every row
 * is a leaf (no further drilldown). Empty rows keep the bar view until data exists.
 */
export function shouldSwitchBarLevelToHoldings(
  records: DiversificationItem[],
): boolean {
  if (records.length === 0) return false;
  return !records.some(recordHasDiversificationDrilldown);
}

/**
 * Legacy UI drilldown uses the response `id` field as the next `diversificationId`.
 * In our API responses, `DiversificationId` can refer to a *holding's* diversification
 * bucket (or other grouping) and is not always a valid node id for drilldown.
 */
export function getDrilldownDiversificationId(
  record: DiversificationItem,
): number {
  const candidate = (record as unknown as { DiversificationId?: unknown })
    .DiversificationId;
  const hasTreeIds =
    typeof candidate === 'number' &&
    Number.isFinite(candidate) &&
    candidate > 0 &&
    typeof (record as unknown as { ParentId?: unknown }).ParentId ===
      'number' &&
    typeof (record as unknown as { CategoryId?: unknown }).CategoryId ===
      'number' &&
    (record as unknown as { ParentId?: number }).ParentId !== 0;

  // If the response includes diversification tree identifiers, prefer `DiversificationId`
  // because some datasets return a row `id` that isn't a valid node id for drilldown.
  if (hasTreeIds) return candidate;

  if (typeof record.id === 'number' && record.id > 0) return record.id;
  return typeof candidate === 'number' && candidate > 0
    ? candidate
    : PortfolioDiversificationIdNumber.ROOT;
}

export function buildSchemeNameToIdMap(
  schemes: DiversificationScheme[] | undefined,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const rec of schemes ?? []) {
    if (typeof rec.Name !== 'string' || rec.Name.length === 0) continue;
    map.set(normalizeDiversificationName(rec.Name), rec.DiversificationId);
  }
  return map;
}

// TODO: Remove this function once the /diversification endpoint populates CategoryId on every
// DiversificationItem with drilldown:true. When that is done, resolveDrilldownCategoryId will
// always resolve via recordCategoryId (first priority) and the schemeCategoryId fallback will
// never be needed. Steps: (1) backend includes CategoryId on drilldown records, (2) remove
// schemeCategoryId param from resolveDrilldownCategoryId, (3) remove this map and its usages
// in assetAllocationPieChart.tsx (schemeDiversificationIdToCategoryId).
export function buildDiversificationIdToCategoryIdMap(
  schemes: DiversificationScheme[] | undefined,
): Map<number, number> {
  const map = new Map<number, number>();
  for (const rec of schemes ?? []) {
    const cat = rec.DiversificationCategoryId;
    if (typeof cat !== 'number' || !Number.isFinite(cat) || cat <= 0) continue;
    const existing = map.get(rec.DiversificationId);
    if (existing === undefined || cat < existing) {
      map.set(rec.DiversificationId, cat);
    }
  }
  return map;
}

export function resolveAccountRootDrilldownId(
  record: DiversificationItem,
  schemeRootNameToId: Map<string, number>,
): number {
  // A closed slice stays closed, even when its name matches a scheme category.
  if (!recordHasDiversificationDrilldown(record)) {
    return getDrilldownDiversificationId(record);
  }

  // For grouped account-root rows, we only get `name` + chart fields, and `id` is a grouping key.
  // Map the slice name back to a real diversification node id using the scheme table.
  const hasTreeIds =
    typeof (record as unknown as { DiversificationId?: number })
      .DiversificationId === 'number' &&
    typeof (record as unknown as { ParentId?: number }).ParentId === 'number' &&
    typeof (record as unknown as { CategoryId?: number }).CategoryId ===
      'number' &&
    (record as unknown as { DiversificationId?: number }).DiversificationId !==
      0;
  if (hasTreeIds) return getDrilldownDiversificationId(record);

  const key = normalizeDiversificationName(record.name);
  const mapped = schemeRootNameToId.get(key);
  const fallbackId = getDrilldownDiversificationId(record);
  return typeof mapped === 'number' && mapped > 0 ? mapped : fallbackId;
}

function getDefaultCategoryId(
  categories: DiversificationCategory[] | undefined,
  diversificationId: number,
): number | null {
  let min: number | undefined;
  for (const c of categories ?? []) {
    if (c.ParentDiversificationId !== diversificationId) continue;
    if (min === undefined || c.DiversificationCategoryId < min) {
      min = c.DiversificationCategoryId;
    }
  }
  return min ?? null;
}

function coercePositiveInt(
  value: number | undefined | null,
): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : undefined;
}

export function resolveDrilldownCategoryId(params: {
  recordCategoryId: number | undefined | null;
  currentLevelCategoryId: number | undefined | null;
  categories: DiversificationCategory[] | undefined;
  diversificationId: number;
  schemeCategoryId?: number | null;
  /**
   * At the root pie level we intentionally compute a default category id (legacy behavior),
   * rather than attempting to reuse any possibly-stale category id.
   */
  isRootPie: boolean;
}): number | null {
  const defaultCategoryId = getDefaultCategoryId(
    params.categories,
    params.diversificationId,
  );
  const schemeFallback = coercePositiveInt(params.schemeCategoryId) ?? null;
  const bestDefault =
    defaultCategoryId != null && defaultCategoryId > 0
      ? defaultCategoryId
      : schemeFallback;

  if (params.isRootPie) return bestDefault;

  const recordId = coercePositiveInt(params.recordCategoryId);
  if (recordId !== undefined) return recordId;

  const currentId = params.currentLevelCategoryId;
  if (currentId != null && currentId > 0) return currentId;

  return bestDefault;
}

/**
 * Account-root pie fetches a sub-classification grouping and the saved allocation.
 * Saved rows win. The grouped response is only for manual accounts that return
 * nothing unless grouped.
 */
export function pickAccountRootDiversificationData(
  grouped: DiversificationResponse | undefined,
  ungrouped: DiversificationResponse | undefined,
): DiversificationResponse | undefined {
  const groupedRecords = grouped?.Records ?? [];
  const ungroupedRecords = ungrouped?.Records ?? [];

  if (ungroupedRecords.length > 0) {
    return ungrouped;
  }
  if (groupedRecords.length > 0) {
    return grouped;
  }
  return ungrouped ?? grouped;
}
